import { ICellRendererParams } from '@ag-grid-community/core';
import { ILoadBoardViewModel } from '@store/loadboard';
import {
  PhoneIcon,
  FileDocumentIcon,
  FlagIcon,
  FireIcon,
  ChatQuestionIcon,
  AlertOutlineIcon,
  AlertOctagonIcon,
  EyeOutlinedIcon,
} from '@atgfr8/icons';
import { Popover, Typography } from '@atgfr8/atg-ui-tailwind';
import { For, JSXElement, createMemo, createSignal } from 'solid-js';

type FlagDataT = {
  flagType: string;
  flagText?: string | null | undefined;
  currentState?: string | null | undefined;
};

type FlagTypePropsT = {
  data: FlagDataT | undefined;
  readLatest: () => FlagDataT;
};

const toFlagData = (
  flagType: string | null | undefined,
  flagText: string | null | undefined,
  currentState: string | null | undefined,
): FlagDataT => ({
  flagType: flagType ?? '',
  flagText,
  currentState,
});

export const FlagTypeCell = (
  data: ICellRendererParams<ILoadBoardViewModel>,
) => {
  return (
    <FlagType
      data={toFlagData(
        data.data?.flagType,
        data.data?.flagText,
        data.data?.currentState,
      )}
      readLatest={() => {
        const row = data.node.data;
        return toFlagData(row?.flagType, row?.flagText, row?.currentState);
      }}
    />
  );
};

export const AllFlagsTypeCell = (
  data: ICellRendererParams<ILoadBoardViewModel>,
) => {
  return (
    <For each={data.data?.orderFlags ?? []}>
      {(flag) => {
        return (
          <FlagType
            data={toFlagData(flag.flagType, flag.flagText, flag.currentState)}
            readLatest={() => {
              const latest = data.node.data?.orderFlags.find(
                (item) => item.id === flag.id,
              );
              const source = latest ?? flag;
              return toFlagData(
                source.flagType,
                source.flagText,
                source.currentState,
              );
            }}
          />
        );
      }}
    </For>
  );
};

export const FlagType = (props: FlagTypePropsT) => {
  const [anchorEl, setAnchorEl] = createSignal<Element | null>(null);

  const popoverFlag = createMemo(() => {
    if (anchorEl() == null) {
      return undefined;
    }
    return props.readLatest();
  });
  let iconStyle: JSXElement | null = null;

  const handlePopoverOpen = (event: { currentTarget: Element }) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = () => Boolean(anchorEl());

  const getFlagIcon = (flagColor: string) => {
    return (
      <FlagIcon
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        aria-owns={open() ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        style={{ color: flagColor }}
      />
    );
  };
  const getReportIcon = (flagColor: string) => {
    return (
      <AlertOctagonIcon
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        aria-owns={open() ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        style={{ color: flagColor }}
      />
    );
  };
  // Reviewed with James Approved
  // eslint-disable-next-line complexity
  const getIconType = () => {
    switch (props.data?.flagType) {
      case 'Red Flag':
        iconStyle = getFlagIcon('#E09A9A');
        break;
      case 'Blue Flag':
        iconStyle = getFlagIcon('#8BD0FF');
        break;
      case 'Green Flag':
        iconStyle = getFlagIcon('#129208');
        break;
      case 'Yellow Flag':
        iconStyle = getFlagIcon('#d6d31a');
        break;
      case 'Orange Flag':
        iconStyle = getFlagIcon('#f3b42d');
        break;
      case 'Escalated':
        iconStyle = getReportIcon('#B00020');
        break;
      case 'Hot Load':
        iconStyle = (
          <FireIcon
            size={28}
            style={{ color: 'crimson' }}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
        break;
      case 'Uploaded':
        iconStyle = (
          <FileDocumentIcon
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
        break;
      case 'Tender Updated':
        iconStyle = (
          <AlertOutlineIcon
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            style={{ color: '#d6d31a' }}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
        break;
      case 'Needs approval':
        iconStyle = (
          <ChatQuestionIcon
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
        break;
      case 'Needs Review':
        iconStyle = (
          <EyeOutlinedIcon
            style={{ color: '#5E35B1' }}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
        break;
      case 'Get Paperwork':
      case 'Refund Request':
      case 'EFS Issued':
      case 'Missing Paperwork':
        iconStyle = (
          <PhoneIcon
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            aria-haspopup="true"
            aria-owns={open() ? 'mouse-over-popover' : undefined}
          />
        );
    }
    return iconStyle as JSXElement;
  };

  return (
    <div>
      {getIconType()}
      <Popover
        id="mouse-over-popover"
        sx={{ 'pointer-events': 'none' }}
        open={open()}
        anchorEl={anchorEl()}
        dataTestId="flag-type-cell-popover"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div
          style={{
            padding: '8px',
            'max-width': '300px',
            'overflow-wrap': 'anywhere',
          }}
        >
          {popoverFlag()?.currentState ? (
            <>
              <Typography sxProps={{ fontWeight: 'bold' }}>
                {popoverFlag()?.currentState}
              </Typography>
              {popoverFlag()?.flagText && (
                <Typography sxProps={{ marginTop: '4px' }}>
                  {popoverFlag()?.flagText}
                </Typography>
              )}
            </>
          ) : popoverFlag()?.flagText !== null &&
            popoverFlag()?.flagText !== '' ? (
            <Typography>{popoverFlag()?.flagText}</Typography>
          ) : (
            <Typography>{popoverFlag()?.flagType}</Typography>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default FlagTypeCell;
